import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, ApiError } from '@/lib/api.js';
import './ArchivePage.css';
import './FdrPage.css';

const OPTIONS = [1, 2, 3, 4, 5];

/*
 * Fixture difficulty ratings for one user's ticker.
 *
 * Saving requires being signed in as that user; the API enforces it and the
 * form only offers the button when the session matches. The old page
 * reported the result with a window.alert — it is an inline status now.
 */
export default function FdrPage() {
	const { user } = useParams();
	const [rows, setRows] = useState([]);
	const [status, setStatus] = useState('loading');
	const [error, setError] = useState(null);
	const [canSave, setCanSave] = useState(false);
	const [saveState, setSaveState] = useState({ status: 'idle', message: null });

	useEffect(() => {
		Promise.all([api.get(`/api/fdr/${user}`), api.get('/api/status')])
			.then(([fdr, session]) => {
				setRows(Array.isArray(fdr) ? fdr : []);
				setCanSave(Boolean(session?.status) && session?.id === user);
				setStatus('ready');
			})
			.catch((err) => {
				setError(err instanceof ApiError ? err.message : 'Could not load these ratings.');
				setStatus('error');
			});
	}, [user]);

	const setRating = (team, venue, value) => {
		setRows((prev) =>
			prev.map((row) => (row.team === team ? { ...row, [venue]: Number(value) } : row)),
		);
		setSaveState({ status: 'idle', message: null });
	};

	const save = () => {
		setSaveState({ status: 'saving', message: null });
		api
			.post(
				`/api/fdr/${user}`,
				rows.map(({ team, home, away }) => ({ team, home, away })),
			)
			.then(() => setSaveState({ status: 'saved', message: 'Ratings saved.' }))
			.catch((err) =>
				setSaveState({
					status: 'error',
					message: err instanceof ApiError ? err.message : 'Could not save.',
				}),
			);
	};

	if (status === 'loading') {
		return (
			<div className="tab-loading" role="status">
				<span className="spinner" aria-hidden="true" />
				<span>Loading ratings…</span>
			</div>
		);
	}

	if (status === 'error') {
		return (
			<div className="notice notice--error" role="alert">
				<strong>Could not load these ratings.</strong>
				<span>{error}</span>
			</div>
		);
	}

	return (
		<div className="archive-page">
			<header className="archive-page__intro">
				<h1>Fixture difficulty</h1>
				<p className="archive-page__lede">
					How hard each team is to face, home and away. These ratings drive{' '}
					<Link to="/ticker">the ticker</Link>.
				</p>
			</header>

			<div className="scroll-x">
				<table className="fdr-table">
					<thead>
						<tr>
							<th scope="col">Team</th>
							<th scope="col">Home</th>
							<th scope="col">Away</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr key={row.team}>
								<th scope="row" className="fdr-table__team">
									{row.team}
								</th>
								{['home', 'away'].map((venue) => (
									<td key={venue}>
										<select
											className="fdr-select"
											value={row[venue]}
											data-difficulty={row[venue]}
											disabled={!canSave}
											aria-label={`${row.team} ${venue} difficulty`}
											onChange={(e) => setRating(row.team, venue, e.target.value)}
										>
											{OPTIONS.map((n) => (
												<option key={n} value={n}>
													{n}
												</option>
											))}
										</select>
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{canSave ? (
				<div className="fdr-actions">
					<button
						className="btn btn--primary"
						type="button"
						onClick={save}
						disabled={saveState.status === 'saving'}
					>
						{saveState.status === 'saving' ? 'Saving…' : 'Save ratings'}
					</button>
					{saveState.message && (
						<span
							className={
								saveState.status === 'error' ? 'fdr-status fdr-status--error' : 'fdr-status'
							}
							role="status"
						>
							{saveState.message}
						</span>
					)}
				</div>
			) : (
				<p className="section-note">
					Sign in as {user} to change these ratings.
				</p>
			)}
		</div>
	);
}
